import { Gamepad2, NotebookPen, ShieldCheck } from 'lucide-react'
import SteamIcon from '@/components/site/SteamIcon'
import { panelCard } from '@/components/layout/Panel'
import { memberSteamProfileUrl } from '@/lib/steam'
import { useLanguage } from '@/contexts/LanguageContext'

export interface Member {
  id: string
  display_name: string
  username: string
  avatar_url: string
  team: 'TLL' | 'TLLA'
  steam_linked?: boolean
  steam_name?: string
  // Staff only — /api/members leaves it out for everyone else.
  steam_id?: string
  steam_verified?: boolean
  // Staff only, like steam_id: how many staff notes this member has.
  note_count?: number
  // WARDOGS time from Steam (backend/members/steam_playtime.py): lifetime and
  // the last two weeks, in minutes, exactly as Steam reports them. Absent when
  // Steam is not linked or not read yet.
  wardogs_playtime?: WardogsPlaytime | null
  // When they entered the clan, epoch seconds; 0 = unknown (predates the table).
  clan_joined_at?: number
}

export type WardogsPlaytime =
  | { total: number; two_weeks: number }
  | { private: true }

/** Dakika → "4,5 sa" / "12 sa" / "25 dk" — ondalık yalnızca 10 saatin altında. */
export function formatPlaytime(minutes: number, T: ReturnType<typeof useLanguage>['T'], lang: string): string {
  if (minutes < 60) return T.members.playtimeMinutes.replace('{n}', String(minutes))
  const h = minutes / 60
  const n = h < 10
    ? h.toLocaleString(lang, { maximumFractionDigits: 1 })
    : String(Math.round(h))
  return T.members.playtimeHours.replace('{n}', n)
}

/**
 * Rozet — panelin dört köşeli hali. shadcn `Badge`'i `rounded-full` ve burada
 * yuvarlak bir tek şey var: avatar. Rozetler kartın kendi diline uyar.
 */
const badge = 'shrink-0 rounded-sm border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em]'

const ROLE_BADGE_COLORS: Record<string, string> = {
  owner:      'border-yellow-500/40 bg-yellow-500/10 text-yellow-300',
  admin:      'border-purple-500/40 bg-purple-500/10 text-purple-300',
  moderator:  'border-blue-500/40 bg-blue-500/10 text-blue-300',
  strategist: 'border-teal-400/40 bg-teal-400/10 text-teal-300',
  member:     'border-white/15 bg-white/5 text-muted-foreground',
}


interface MemberCardProps {
  member: Member
  lastSeen: number
  isOnline: boolean
  // Dashboard role, resolved from the member's Discord roles. Display only:
  // it is granted and revoked in Discord, never from this page.
  canSeeRoles: boolean
  memberRole: string
  // The SteamID64 is what the game server and the reserved slots key on, so only
  // staff see it. Members see the account name, which is what identifies it.
  canSeeSteamId: boolean
  // Staff: opens the notes + manual SteamID panel. Omitted for everyone else.
  onManage?: () => void
}

export default function MemberCard({
  member, lastSeen, isOnline, canSeeRoles, memberRole, canSeeSteamId, onManage,
}: MemberCardProps) {
  const { T, lang } = useLanguage()
  const playtime = member.wardogs_playtime

  const formatLastSeen = (ts: number): string => {
    if (!ts || ts === 0) return T.members.never
    const diff = Math.floor(Date.now() / 1000 - ts)
    if (diff < 60) return T.members.justNow
    if (diff < 3600) return T.members.mAgo.replace('{n}', String(Math.floor(diff / 60)))
    if (diff < 86400) return T.members.hAgo.replace('{n}', String(Math.floor(diff / 3600)))
    if (diff < 86400 * 30) return T.members.dAgo.replace('{n}', String(Math.floor(diff / 86400)))
    return T.members.moAgo.replace('{n}', String(Math.floor(diff / 86400 / 30)))
  }

  const lastSeenLabel = formatLastSeen(lastSeen)

  return (
    <div className={`${panelCard} p-3.5 transition-colors hover:border-white/25`}>
      {/* Header */}
      <div className="flex items-center gap-3">
        <img
          src={member.avatar_url}
          alt={member.display_name}
          className="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-white/15"
          onError={(e) => {
            ;(e.target as HTMLImageElement).src =
              'https://cdn.discordapp.com/embed/avatars/0.png'
          }}
        />
        <div className="min-w-0 flex-1">
          {/* Discord adı veriden gelir — büyütülmez (bkz. i18n bölümü). */}
          <p className="truncate text-sm font-semibold leading-tight">{member.display_name}</p>
          <p className="truncate text-xs text-muted-foreground">@{member.username}</p>
        </div>
        <span
          className={`${badge} ${
            member.team === 'TLL'
              ? 'border-primary/50 bg-primary/10 text-primary'
              : 'border-sky-300/40 bg-sky-300/10 text-sky-300'
          }`}
        >
          {member.team}
        </span>
        {canSeeRoles && memberRole !== 'member' && (
          <span className={`${badge} ${ROLE_BADGE_COLORS[memberRole] ?? ROLE_BADGE_COLORS.member}`}>
            {T.roles[memberRole as keyof typeof T.roles] ?? memberRole}
          </span>
        )}
      </div>

      {/* Last seen */}
      <div className="mt-2.5 flex items-center gap-1.5">
        {isOnline && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />}
        <span className={`text-[10px] font-medium uppercase tracking-[0.1em] ${
          isOnline ? 'text-emerald-400' : 'text-muted-foreground/60'
        }`}>
          {isOnline ? T.members.online : T.members.lastSeen.replace('{t}', lastSeenLabel)}
        </span>
        {/* Gizli profil 0 değildir: Steam bize hiçbir şey söylemedi. */}
        {playtime && (
          'private' in playtime ? (
            <span
              title={T.members.playtimePrivateHint}
              className="ml-auto flex items-center gap-1 text-[10px] text-muted-foreground/50"
            >
              <Gamepad2 className="h-3 w-3" />
              {T.members.playtimePrivate}
            </span>
          ) : (
            <span className="ml-auto flex items-center gap-1 text-[10px] tabular-nums">
              <Gamepad2 className="h-3 w-3 text-muted-foreground/60" />
              <span
                title={T.members.playtimeTotalHint}
                className={playtime.total ? 'text-foreground/80' : 'text-muted-foreground/50'}
              >
                <span className="text-muted-foreground/60">{T.members.playtimeTotal} </span>
                {formatPlaytime(playtime.total, T, lang)}
              </span>
              <span className="text-muted-foreground/40">·</span>
              <span
                title={T.members.playtimeFortnightHint}
                className={playtime.two_weeks ? 'text-foreground/80' : 'text-muted-foreground/50'}
              >
                <span className="text-muted-foreground/60">{T.members.playtimeFortnight} </span>
                {formatPlaytime(playtime.two_weeks, T, lang)}
              </span>
            </span>
          )
        )}
        {onManage && (
          <button
            type="button"
            onClick={onManage}
            title={T.members.manage.open}
            aria-label={T.members.manage.open}
            className={`${playtime ? '' : 'ml-auto '}flex cursor-pointer items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] transition-colors hover:bg-white/10 hover:text-foreground ${
              member.note_count ? 'text-primary' : 'text-muted-foreground/60'
            }`}
          >
            <NotebookPen className="h-3 w-3" />
            {member.note_count ? member.note_count : null}
          </button>
        )}
      </div>

      {/* Steam link — read-only on the card.
          Members link through Steam's own sign-in; staff can also enter an id
          by hand from the manage panel, and that one stays "unverified" until
          its owner proves it — the reserved-slot sync keys on the difference.

          The account name is the door to the profile for everyone. The id
          still goes only to staff (`/api/members` leaves it out for the rest),
          so the address is built by the backend instead of the browser:
          /api/members/<id>/steam bounces to Steam. Handing the whole roster
          everyone's SteamID64 just to make a link clickable was the trade we
          did not want; this one costs a redirect. */}
      <div className="mt-2 flex items-center gap-2 border-t border-white/8 pt-2">
        <SteamIcon size={12} />
        {member.steam_linked ? (
          <>
            <a
              href={memberSteamProfileUrl(member.id)}
              target="_blank"
              rel="noreferrer"
              title={T.members.steamOpenProfile}
              className="truncate text-xs text-muted-foreground underline decoration-dotted underline-offset-2 transition-colors hover:text-foreground"
            >
              {member.steam_name || T.members.steamNoName}
            </a>
            {member.steam_verified ? (
              <ShieldCheck className="h-3 w-3 shrink-0 text-primary" aria-label={T.members.steamVerified} />
            ) : (
              <span className={`${badge} border-amber-500/40 bg-amber-500/10 text-amber-400`}>
                {T.members.steamUnverified}
              </span>
            )}
          </>
        ) : (
          <span className="text-xs text-muted-foreground/60">{T.members.steamNotLinked}</span>
        )}
      </div>

      {canSeeSteamId && member.steam_id && (
        <p className="mt-1 select-all pl-[18px] font-mono text-[10px] tracking-tight text-muted-foreground/50">
          {member.steam_id}
        </p>
      )}
    </div>
  )
}
